<template>
  <div style="margin:20px">
    <FiPageRail v-if="!standalone" title="Active DVIR Defects">
      <template #actions>
        <b-btn size="sm" variant="outline-primary" @click="downloadCsv()" :disabled="!items.length" aria-label="download CSV"><i class="fa fa-download"></i></b-btn>&nbsp;
        <b-btn size="sm" variant="success" @click="view_more_info = !view_more_info" aria-label="more info"><i class="fa fa-info-circle"></i></b-btn>
      </template>
    </FiPageRail>

    <div v-if="standalone">
      <span style="font-size: 20px; font-weight: bold; margin-bottom: 10px;" class="d-flex align-items-center justify-content-center">
        <a target="_new" href="https://www.initekconsulting.com/fleet-integrations"><img style="margin-top:-5px" width="40" src="favicon.ico"/></a>&nbsp;Active DVIR Defects&nbsp;&nbsp;
        <b-spinner v-if="reloading" style="width: 1.8rem; height: 1.8rem"></b-spinner><span v-else style="width: 1.8rem; height: 1.8rem">&nbsp;</span>
      </span>
    </div>

    <b-modal title="More Info" size="lg" v-model="view_more_info" ok-only>
      <p>Every open DVIR defect across the fleet, pulled live from the ELD/telematics provider (Saucon). Statuses are shown in the provider's own vocabulary.</p>
      <ul>
        <li><b>Open</b> &mdash; driver-reported, not yet reviewed by a mechanic</li>
        <li><b>Repair pending</b> &mdash; mechanic acknowledged, repair/parts in progress</li>
        <li><b>Does not need repair</b> / <b>Repaired</b> &mdash; cleared (not shown here; this list is active defects only)</li>
      </ul>
      <hr/>
      <h5>Standalone / TV mode</h5>
      refreshes every minute, no login required
      <br/><a target="_blank" :href="'./#/veh-defects/' + standaloneId">click here to load the standalone view</a>
      <vsm-standalone-url-copy-button :href="'./#/veh-defects/' + standaloneId" :tenant-id="tenant_id"/>
    </b-modal>

    <div v-if="!loading && summary" class="vd-kpis">
      <div class="vd-kpi"><b>{{ summary.vehicle_count }}</b>buses with open defects</div>
      <div class="vd-kpi"><b>{{ summary.defect_count }}</b>open defects</div>
      <div class="vd-kpi vd-kpi--major" v-if="summary.major_count"><b>{{ summary.major_count }}</b>major</div>
      <div class="vd-kpi"><b>{{ summary.minor_count }}</b>minor</div>
      <div class="vd-spacer"></div>
      <b-form-input v-model="search_filter" size="sm" placeholder="Filter..." class="vd-search" aria-label="filter defects"/>
      <b-btn size="sm" variant="light" @click="load_data()" aria-label="reload"><i class="fa fa-refresh"></i></b-btn>
    </div>

    <div v-if="summary">
      <b-table :busy="loading" sticky-header="72vh" bordered small responsive
               :items="filtered_items" :fields="fields" :sort-compare="tableSortCompare"
               class="vd-table">
        <template #table-busy>
          <div class="text-center text-danger my-2">
            <b-spinner style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
          </div>
        </template>

        <template slot="vehicle" slot-scope="row">
          <span class="vd-bus">{{ row.item.vehicle }}</span>
        </template>

        <template slot="severity" slot-scope="row">
          <span class="vd-pill" :class="row.item.severity && row.item.severity.toLowerCase() === 'major' ? 'vd-major' : 'vd-minor'">
            {{ row.item.severity | capitalize }}
          </span>
        </template>

        <template slot="status_label" slot-scope="row">
          <span class="vd-status">{{ row.item.status_label }}</span>
        </template>

        <template slot="reported_time" slot-scope="row">
          <span class="vd-nowrap">{{ row.item.reported_time | formatDate }}</span>
        </template>

        <template slot="comments" slot-scope="row">
          <span class="vd-comments">{{ row.item.comments }}</span>
        </template>
      </b-table>

      <div v-if="!filtered_items.length && !loading" class="text-center text-muted my-4">
        No active defects{{ search_filter ? ' match your filter' : '' }}.
      </div>
    </div>
    <div v-else>
      <div class="text-center text-danger my-2">
        <b-spinner style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
      </div>
    </div>
  </div>
</template>

<script>
import { get_dvir_defects } from '../networking/apis'
import { mapState } from 'vuex'
import VsmStandaloneUrlCopyButton from './VsmStandaloneUrlCopyButton.vue'

export default {
  name: 'vehicle_defects',
  components: { VsmStandaloneUrlCopyButton },
  metaInfo: {
    title: 'Fleet Integrations',
    titleTemplate: 'Fleet Integrations',
  },
  filters: {
    capitalize(v) {
      if (!v) return ''
      return v.charAt(0).toUpperCase() + v.slice(1)
    },
  },
  data() {
    return {
      standalone: false,
      view_more_info: false,
      search_filter: '',
      tenant_id: null,
      reloading: false,
      reload_mode: 0,
      timer: null,
      items: [],
      summary: null,
      loading: true,
      fields: [
        { key: 'vehicle', label: 'Bus', sortable: true, thStyle: { width: '70px' } },
        { key: 'defect_reason', label: 'Defect', sortable: true },
        { key: 'inspection_point', label: 'Inspection point', sortable: true },
        { key: 'severity', label: 'Severity', sortable: true, thStyle: { width: '90px' } },
        { key: 'status_label', label: 'Status', sortable: true, thStyle: { width: '120px' } },
        { key: 'comments', label: 'Driver comments', sortable: false },
        { key: 'driver', label: 'Driver', sortable: true, thStyle: { width: '130px' } },
        { key: 'reported_time', label: 'Reported', sortable: true, thStyle: { width: '100px' } },
        { key: 'work_order', label: 'WO#', sortable: true, thStyle: { width: '70px' } },
      ],
    }
  },
  async created() {
    this.tenant_id = this.user && this.user.tenant
    await this.check_features()
  },
  beforeDestroy() {
    this.stopTimer()
  },
  computed: {
    ...mapState({
      user: state => state.profile.user,
    }),
    standaloneId() {
      return (this.summary && this.summary.vsm_standalone_uuid) || this.tenant_id
    },
    filtered_items() {
      const q = (this.search_filter || '').toLowerCase().trim()
      if (!q) return this.items
      return this.items.filter(i =>
        ['vehicle', 'defect_reason', 'inspection_point', 'status_label', 'comments', 'driver', 'work_order']
          .some(k => (i[k] || '').toString().toLowerCase().indexOf(q) !== -1))
    },
  },
  methods: {
    startTimer() {
      this.stopTimer()
      if (this.reload_mode > 0) {
        this.timer = setInterval(() => this.reload_data(), this.reload_mode)
      }
    },
    stopTimer() {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },
    async check_features() {
      if (this.$route.path.indexOf('/veh-defects/') !== -1) {
        this.standalone = true
        this.tenant_id = this.$route.path.split('/veh-defects/')[1]
        this.reload_mode = 60000
      }
      await this.load_data()
    },
    async load_data() {
      this.loading = true
      try {
        const { value: { data = {} } } = await get_dvir_defects(this.tenant_id)
        this.summary = data.summary
        this.items = data.results || []
        this.startTimer()
      } catch (e) {
        this.$emit('showAlert', 'error retrieving DVIR defects, check your URL or contact support if needed', 'danger', 5000)
        this.stopTimer()
      }
      this.loading = false
    },
    async reload_data() {
      if (this.$pageVisibility && this.$pageVisibility.hidden) return
      if (this.reloading) return
      this.reloading = true
      try {
        const { value: { data = {} } } = await get_dvir_defects(this.tenant_id)
        this.summary = data.summary
        this.items = data.results || []
      } catch (e) {
        // keep last-good data on a transient reload failure
      }
      this.reloading = false
    },
    tableSortCompare(a, b, key) {
      // numeric-aware sort for bus/WO columns that are really numbers-as-strings
      if (key === 'vehicle' || key === 'work_order') {
        const na = parseFloat(a[key]), nb = parseFloat(b[key])
        if (!isNaN(na) && !isNaN(nb)) return na - nb
      }
      return null
    },
    downloadCsv() {
      const cols = [
        ['vehicle', 'Bus'], ['vin', 'VIN'], ['defect_reason', 'Defect'],
        ['inspection_point', 'Inspection point'], ['severity', 'Severity'],
        ['status_label', 'Status'], ['comments', 'Driver comments'],
        ['driver', 'Driver'], ['reported_time', 'Reported'], ['work_order', 'WO#'],
      ]
      const esc = v => '"' + (v == null ? '' : String(v)).replace(/"/g, '""') + '"'
      const lines = [cols.map(c => esc(c[1])).join(',')]
      this.filtered_items.forEach(i => lines.push(cols.map(c => esc(i[c[0]])).join(',')))
      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'active_dvir_defects.csv'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    },
  },
  watch: {
    user: {
      deep: true,
      handler() {
        if (!this.standalone) {
          this.tenant_id = this.user && this.user.tenant
          this.check_features()
        }
      },
    },
    '$pageVisibility.hidden'(newVal, oldVal) {
      if (oldVal && !newVal && this.reload_mode > 0) {
        this.reload_data()
      }
    },
  },
}
</script>

<style scoped>
.vd-kpis {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 6px 4px 14px;
  flex-wrap: wrap;
}
.vd-kpi {
  background: #f3f4f6;
  border-radius: 8px;
  padding: 6px 14px;
  font-size: 13px;
  color: #374151;
}
.vd-kpi b {
  font-size: 18px;
  display: block;
  line-height: 1.1;
}
.vd-kpi--major b { color: #991b1b; }
.vd-spacer { flex: 1 1 auto; }
.vd-search { max-width: 220px; }
.vd-bus { font-weight: 700; }
.vd-comments { color: #4b5563; }
.vd-nowrap { white-space: nowrap; }
.vd-status { font-size: 12px; }
.vd-pill {
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}
.vd-minor { background: #fef3c7; color: #92400e; }
.vd-major { background: #fee2e2; color: #991b1b; }
.vd-table >>> td { vertical-align: middle; }
</style>
